/**
 * Phone number formatting helpers.
 *
 * Use these everywhere a phone number is displayed so the app shows numbers
 * consistently regardless of whether they were stored as E.164 (`+12395550123`),
 * raw digits (`2395550123`), or already-formatted strings (`(239) 555-0123`).
 *
 * Storage format is left untouched — this is purely for display.
 */

/**
 * Strip every character that isn't a digit or a leading `+` so we can reason
 * about the number's length and country code.
 */
function normalize(raw: string): string {
  const trimmed = raw.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  return hasPlus ? `+${digits}` : digits;
}

/**
 * Format a phone number for display.
 *
 * - 10 digits           -> `(NPA) NXX-XXXX`
 * - 11 digits w/ US `1` -> `(NPA) NXX-XXXX`
 * - E.164 US (`+1...`)  -> `(NPA) NXX-XXXX`
 * - E.164 non-US        -> `+CC XXX XXX XXXX` (best-effort grouping)
 * - Anything else       -> returned trimmed; never throws.
 *
 * Returns an empty string for nullish/empty input so it can be used directly
 * inside JSX guards (`{phone && formatPhoneNumber(phone)}`).
 */
export function formatPhoneNumber(raw: string | null | undefined): string {
  if (!raw) return '';

  const normalized = normalize(raw);
  if (!normalized) return '';

  // E.164 / international (starts with +)
  if (normalized.startsWith('+')) {
    const digits = normalized.slice(1);

    // US/Canada: +1XXXXXXXXXX
    if (digits.length === 11 && digits.startsWith('1')) {
      return formatUs10(digits.slice(1));
    }

    // Non-US E.164 — group the subscriber number into 3/3/4-ish chunks.
    // Heuristic: assume a 1-3 digit country code, then the rest is the number.
    return formatInternational(digits);
  }

  // Plain digits
  if (normalized.length === 10) {
    return formatUs10(normalized);
  }

  if (normalized.length === 11 && normalized.startsWith('1')) {
    return formatUs10(normalized.slice(1));
  }

  if (normalized.length === 7) {
    return `${normalized.slice(0, 3)}-${normalized.slice(3)}`;
  }

  // Unknown shape — return the original input trimmed so we don't lose info.
  return raw.trim();
}

function formatUs10(digits: string): string {
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Best-effort grouping for non-US E.164 numbers. We don't ship a full country
 * code table, so we assume a 1-3 digit country code and group the rest as
 * `XXX XXX XXXX` style chunks of three (last group may be 2-4 digits).
 */
function formatInternational(digits: string): string {
  if (digits.length <= 4) return `+${digits}`;

  // Pick country code length: prefer 1, then 2, then 3 based on remaining length.
  const ccLen = digits.length >= 11 ? 2 : digits.length >= 9 ? 1 : 1;
  const cc = digits.slice(0, ccLen);
  const rest = digits.slice(ccLen);

  const groups: string[] = [];
  let i = 0;
  while (i < rest.length) {
    const remaining = rest.length - i;
    const take = remaining > 4 ? 3 : remaining;
    groups.push(rest.slice(i, i + take));
    i += take;
  }

  return `+${cc} ${groups.join(' ')}`;
}

/**
 * Progressive formatter for as-you-type US/Canada phone ENTRY, returning
 * `(XXX) XXX-XXXX` as far as the digits go.
 *
 * Crucially, it drops a leading North-American country code (`1`) BEFORE
 * truncating to 10 digits. Browser autofill and paste often deliver the full
 * `+1 555 123 4567` / `15551234567` (11 digits); a naive `slice(0, 10)` keeps
 * the leading `1` and throws away the LAST digit, producing a wrong, invalid
 * number like `(155) 512-3456`. This mirrors the leading-`1` handling already in
 * `isValidPhoneNumber` and `formatPhoneNumber`, so entry, validation and display
 * all agree.
 */
export function formatUsPhoneInput(raw: string): string {
  let digits = (raw || '').replace(/\D/g, '');
  // Drop a leading US/Canada country code so an autofilled "+1…" doesn't push
  // the last digit out of the 10-digit window.
  if (digits.length > 10 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  const limited = digits.slice(0, 10);
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

/** Maximum significant digits in a phone number (ITU-T E.164). */
export const MAX_PHONE_DIGITS = 15;
/** Minimum digits we treat as a usable phone number (NANP local minimum). */
export const MIN_PHONE_DIGITS = 10;

/** Count just the digits in a free-form phone string. */
export function countPhoneDigits(raw: string | null | undefined): number {
  return (raw || '').replace(/\D/g, '').length;
}

/**
 * Sanitize free-form phone input as the user types. Keeps digits and common
 * separators (space, parens, dot, dash), allows a single leading `+` for
 * international numbers, and caps the digit count at the E.164 maximum so the
 * field can't accept an unrealistic number of digits.
 */
export function sanitizePhoneInput(raw: string): string {
  const hasPlus = raw.trimStart().startsWith('+');
  // Strip everything except digits and the separators we allow for readability.
  const allowed = raw.replace(/[^\d\s().-]/g, '');

  let digitCount = 0;
  let body = '';
  for (const ch of allowed) {
    if (ch >= '0' && ch <= '9') {
      if (digitCount >= MAX_PHONE_DIGITS) continue; // drop digits past the cap
      digitCount++;
    }
    body += ch;
  }
  return (hasPlus ? '+' : '') + body;
}

/**
 * Whether a phone string has a plausible number of digits (10-15). Used to flag
 * obviously-invalid numbers (too short, or beyond the E.164 maximum).
 */
export function isPlausiblePhoneNumber(raw: string | null | undefined): boolean {
  const digits = countPhoneDigits(raw);
  return digits >= MIN_PHONE_DIGITS && digits <= MAX_PHONE_DIGITS;
}

/**
 * Build an `href` value safe for `<a href="tel:...">` links. Strips spaces,
 * dashes and parens so the dialer always gets a clean number, but preserves
 * a leading `+` for international dialing.
 */
export function toTelHref(raw: string | null | undefined): string {
  if (!raw) return '';
  const normalized = normalize(raw);
  return normalized ? `tel:${normalized}` : '';
}
